/**
 * renderVennSvg(doc, opts?) — the still picture, drawn once.
 *
 * Phase 2 Task 10's core half (docs/plans/2026-08-26-sharing-phase-2.md).
 * Two callers share this exact function: the browser viewer (Task 11, which
 * renders the string straight into the DOM and adds its own camera as a CSS
 * transform on top) and the `workers/` thumbnail (Task 10's other half,
 * which rasterizes this string to PNG with a bundled font). One picture,
 * drawn once — never two renderers that can drift apart.
 *
 * WHAT THIS BUYS ("label text at rest"), stated plainly per the plan's own
 * charge, so the deferral is a decision and not a gap discovered later:
 *
 *  - Circles and overlap regions are EXACT geometry — true circular arcs via
 *    SVG clip-path intersection and mask subtraction (see "the region paint"
 *    below), not a polygon approximation and not alpha-blended color math.
 *    A region's fill is always one of the token file's pinned lens colors,
 *    chosen by the SET of scoop indices it contains — never a derived
 *    blend, matching tokens.ts's own "designed lens colors... never raw
 *    color math" line.
 *  - Label placement uses regions.ts's own `anchor` — the same point the
 *    app's interactive auto-fit machinery starts from — so "at rest" means
 *    the geometrically natural resting point, not an arbitrary guess.
 *  - Labels ARE fitted to their region's budget now — wrapped to at most
 *    three lines and shrunk until the block they make sits inside the
 *    region, with the whole stack of a region's labels scaled as one
 *    block. That is `fitRegionLabels` below, and it is `labelFit`'s own
 *    rule rather than a second one: the file moved into core precisely so
 *    this function could reach it (issues 300, 289, 230). Until
 *    2026-09-03 this paragraph said the opposite, and it was honest —
 *    every label was set as ONE line at a fixed 22 with no reference to
 *    the budget at all. What retired that decision was not a better
 *    argument but a photograph: Mathew opened his own shared link in
 *    Safari and three labels overlapped into an unreadable smear.
 *  - What this function still does NOT do, on purpose: per-set orbiting
 *    NAME text (the orbit machinery — interactive, stateful across drag
 *    frames, and with no resting answer a still picture could ask for),
 *    and any re-placement of an orphan. A label whose `members` name no
 *    region that currently exists (per document.ts's fork-2 note) is
 *    skipped outright — this function has no interactive history to decide
 *    where a homeless word should re-settle, and inventing one here would
 *    be a second, divergent placement policy the viewer would have to
 *    un-learn.
 *  - THE MEASUREMENT IS A TABLE, NOT A FONT ENGINE, and the difference is
 *    worth knowing before reading a fitted size as a promise. `textWidth.ts`
 *    sums Baloo 2 Regular's own `hmtx` advances — exact against that face
 *    unkerned, within half a percent of it as actually shaped, and an
 *    estimate (within a few percent, measured) against whatever fallback
 *    a browser reaches for when Baloo is not loaded. See that file for the
 *    numbers and for which way each error points.
 *  - The document's own `title` and each set's `name` (the orbiting label)
 *    are NOT rendered — the plan's charge is "circles, overlap regions, and
 *    label text", and both of those are the app's animated-orbit feature,
 *    not the resting venn picture.
 *
 * VIEWBOX / SIZING CONVENTION: the SVG's own coordinate space is the
 * document's own coordinate space — `viewBox="0 0 canvas.width
 * canvas.height"`, `width`/`height` attributes set to the same numbers. Set
 * positions, radii, and region anchors are therefore emitted completely
 * unscaled: every circle already lives in the coordinate system the app
 * edited it in. This makes the "sane document coordinate story" the plan
 * asks for a non-decision — there is exactly one coordinate space, and
 * scaling to a final pixel size is left entirely to the caller: the
 * thumbnail rasterizer picks its own raster dimensions and the viewer
 * scales the whole `<svg>` with CSS, both for free, because SVG's own
 * viewBox scaling already does that job.
 *
 * ONE EXCEPTION, ADDED LATER AND OPT-IN: `opts.viewBox` lets a caller say
 * which PART of that one coordinate space it is looking at. The ink does
 * not move — every circle is still emitted at its document coordinates —
 * so a cropped render and a full one are the same drawing through two
 * windows. The shelf's tile (cowork Phase 3, Task 10) is why: see the
 * option's own comment for the argument, including the caller-side crop
 * that was tried first and rejected.
 *
 * XSS CLOSED STRUCTURALLY (Severino #11), not with a sanitizer, and by TWO
 * complementary means rather than one escape function doing double duty:
 *
 *  1. Label text — the one field this function ever places where a reader
 *     could see it — passes through `escapeXml` before it reaches the
 *     output, and lands ONLY as XML text content; there is no code path
 *     that string-templates a whole element or the document body from
 *     unescaped input.
 *  2. Set ids never reach the output AT ALL, in any form. A set id is a
 *     document-content string, and this function's internal `clip-N`/
 *     `mask-N` identifiers (referenced via `url(#...)`) are minted from the
 *     circle's own ARRAY INDEX, never from `s.id` — so there is no context
 *     (XML attribute, CSS `url()` functional notation, or otherwise) where
 *     a hostile id could land, escaped or not. See the comment at the
 *     clip-id mint site (Bianca's gate, PR #65 note 3) for why "escape it
 *     for THIS context too" was rejected in favor of removing the content
 *     from that position entirely.
 *
 * The function only ever assembles a fixed, hardcoded set of structural
 * nodes (`svg`, `rect`, `circle`, `clipPath`, `mask`, `g`, `text`) — there
 * is no branch that can emit `<script>`, an `on*=` event handler attribute,
 * or `<foreignObject>`, because none of those tag/attribute names ever
 * appear as a template hole; they would have to be hand-added to this
 * file's own source. One wall (plus one structural removal), not a second
 * sanitizer that can drift out of sync with the first.
 *
 * C0 CONTROL CHARACTERS (Ferruccio's gate, PR #65 round three, blocking 2 —
 * a document-contract decision Mathew sanctioned under the PR #27
 * precedent) are a DIFFERENT wall, deliberately not built here a second
 * time. `document.ts`'s `parseDocument` now strips every C0 control
 * (U+0000-0008, 000B, 000C, 000E-001F — XML's own forbidden range, minus
 * tab/LF/CR) from `labels[].text`, `sets[].name`, and `title` at the ONE
 * place every real document passes through. `renderVennSvg` does not
 * re-filter them: every `VennDocumentV1` this function is ever handed in
 * production already came from `parseDocument`, and a hand-built object
 * that skips it (as several of this file's OWN tests deliberately do, to
 * prove the escaping wall above holds regardless) is choosing to bypass
 * that wall on purpose, the same way it can already bypass the id
 * charset or the length caps — this function has never re-validated
 * those either. This is a considered "one wall, at parse" choice, not an
 * oversight: unlike `<`/`>`/`&`/`"`/`'`, a raw C0 control is not a
 * structural metacharacter this function's own escaping exists to
 * neutralize — it is a content-legality question belonging to the
 * document itself, and duplicating that filter here would be exactly the
 * "two walls that can drift out of sync" pattern this whole comment
 * argues against for the escaping case.
 *
 * ID NAMESPACE COLLISIONS ACROSS A LARGER PAGE (Severino's ride of PR #65,
 * finding #1, blocking — the property "no doc content in an id" was right,
 * but the ids it minted were still GLOBAL to whatever HTML document this
 * string ends up inlined into). Task 11's viewer inlines this SVG's raw
 * string straight into the page DOM, and `url(#clip-0)` resolves against
 * the WHOLE PAGE's id space, not a scope private to this `<svg>` — a
 * second venn on the same page, an unrelated icon sprite that happens to
 * define `id="clip-0"`, or even a `<base href>` retargeting fragment
 * resolution, can each silently repaint this picture's regions. Fixed
 * with `opts.idPrefix`: every minted `clip-N`/`mask-N` becomes
 * `${idPrefix}clip-N`/`${idPrefix}mask-N`, defaulting to `''` so every
 * existing golden stays bit-identical. The prefix is CALLER CODE, not
 * document content — Task 11 supplies something like `venn-<vennId>-`,
 * never anything typed into a label — so it does not reopen the exact
 * hole index-minting closed; it is validated against the same closed
 * alphabet as a set id (`[A-Za-z0-9_-]`) anyway, defensively, so even a
 * caller bug can't smuggle a `url()`-breaking character in through this
 * new door. See `renderVennSvg`'s own validation for which failure mode
 * (refuse vs. strip) this module picked and why.
 *
 * TITLE/DESC ARE METADATA, NOT CHROME (Severino's ride, finding #3,
 * advisory — taken): the `<title>`/`<desc>` elements this function emits
 * as the very first children of `<svg>` do NOT contradict the "the
 * document's own `title` is NOT rendered" decision above — that decision
 * is about VISIBLE content on the canvas (the venn picture itself); this
 * is accessibility/unfurl METADATA, a completely different slot a screen
 * reader or a chat unfurl reads and a viewer of the picture never sees
 * painted anywhere. The two decisions coexist on purpose.
 */

import type { VennDocumentV1 } from './document.ts';
import {
  BASE_SIZE,
  FLOOR_SIZE,
  biggestFit,
  drawnSize,
  extentsAt,
  typesetsFor,
  type Typeset,
} from './labelFit.ts';
import {
  BALOO2_ASCENDER,
  BALOO2_DESCENDER,
  BALOO2_UNITS_PER_EM,
} from './baloo2Metrics.ts';
import { regionId, regionsFor, type NamedCircle } from './regions.ts';
import { balooEmWidth } from './textWidth.ts';
import { color } from './tokens.ts';

/**
 * Options for `renderVennSvg`. Every field is caller-controlled CODE, not
 * document content — see the module comment's "ID NAMESPACE COLLISIONS"
 * section for why that distinction matters.
 */
export interface RenderVennSvgOptions {
  /** Namespaces every internally-minted `clip-N`/`mask-N` id as
   *  `${idPrefix}clip-N`/`${idPrefix}mask-N`, so two renders inlined into
   *  the same page never collide. Defaults to `''` (Task 10's original,
   *  unprefixed scheme) so every golden fixture stays bit-identical. */
  idPrefix?: string;
  /**
   * WHICH PART OF THE DRAWING IS BEING LOOKED AT, in the document's own
   * coordinates. Defaults to the whole canvas — the sizing convention the
   * module comment describes, unchanged, so every golden stays bit-
   * identical and both existing callers are untouched.
   *
   * The shelf's tile (cowork Phase 3, Task 10) is what needs it. A tile is
   * a small square of a venn at rest; the document's canvas is whatever
   * window the venn was last edited in, and the venn occupies a fraction of
   * it wherever the maker left it. Rendered at the document frame and
   * scaled down, a venn drawn near one edge becomes a speck in a field of
   * cream. The caller computes the venn's own fit (core's `vennBounds`
   * plus whatever air it wants) and hands it in here.
   *
   * IT CROPS THE FRAME, IT DOES NOT MOVE THE INK. Every circle, mask and
   * label is still emitted at its document coordinates — a tile and a full
   * render are the same drawing seen through two windows, which is what
   * makes "the tile is a real render of the real document" a structural
   * fact rather than a claim. (The ground rect and the mask rects still
   * cover the whole canvas, so a box inside the canvas is fully painted;
   * a box reaching OUTSIDE it — a venn dragged past the edge — shows the
   * page's own background there, exactly as it would in the app.)
   *
   * Like `idPrefix`, this is caller CODE and is REFUSED rather than
   * repaired when it is not a box: a NaN width is a bug in whoever
   * computed the fit, and an invented default would hide it behind a
   * plausible-looking picture.
   */
  viewBox?: { x: number; y: number; width: number; height: number };
}

/** The alphabet a caller's `idPrefix` must stay inside — the same charset
 *  `document.ts`'s `SET_ID_PATTERN` restricts set ids to, applied here for
 *  the identical reason: an id ultimately lands in `url(#...)` functional
 *  notation, and this format's own ids never need `(`, `)`, whitespace, or
 *  any other character that grammar treats specially. A REFUSAL, not a
 *  strip: unlike document content (where stripping an XML-illegal
 *  character is normalizing a lossy paste — see stripXmlIllegalChars's own
 *  argument in document.ts), `idPrefix` is a literal a CALLER writes in
 *  their own source. Silently mangling it would hide a caller bug behind
 *  a plausible-looking id; throwing puts the bug exactly where the caller
 *  can see it, the same way a malformed call to any other internal API
 *  fails loudly rather than being "helpfully" repaired. */
const ID_PREFIX_PATTERN = /^[A-Za-z0-9_-]*$/;

/** THE RESTING SIZE — a ceiling now, not a fixed size (issues 300/289).
 *
 *  A label that fits its region is still set at exactly 22 document units,
 *  which is why every golden whose words already fitted is byte-identical
 *  across the round that taught this function to fit. A label that does
 *  NOT fit is wrapped and shrunk until it does. The ceiling stays 22
 *  rather than rising to the app's `BASE_SIZE` of 28: this renderer's
 *  labels have been 22 since Task 10, that is a decision about pixels, and
 *  pixels are Mathew's.
 *
 *  Emitted with an explicit `px` unit (Severino's ride, finding #4/#5,
 *  advisory — taken): SVG's unitless length and `px` are equivalent by
 *  spec, but stating the unit removes any ambiguity for a renderer that
 *  guesses differently, at zero cost to this file's own users. */
const LABEL_FONT_SIZE = 22;

/** Line pitch, as a multiple of the drawn size — and it is 1.2 rather than
 *  the face's own designed line box on measured grounds, not by habit.
 *
 *  Baloo 2's hhea box is 1.602 em (ascender 1.078 + descender 0.524),
 *  which is a Devanagari-sized box; Latin ink in this face reaches at most
 *  0.670 em above the baseline and 0.202 em below it — 0.872 em all told,
 *  measured through Chrome's `actualBoundingBox*` with the shipped `.ttf`
 *  loaded (2026-09-03, corpus with caps, ascenders and descenders). At a
 *  1.2 em pitch there is still 0.33 em of clear air between one line's
 *  descenders and the next line's ascenders.
 *
 *  The app's `LabelsLayer` takes the designed box instead (it hands
 *  `typesetsFor` whatever `SkFont.getMetrics()` reports), so its blocks are
 *  taller than these for the same words. A deliberate divergence with a
 *  reason on each side, not a drift: the app is fitting text that a hand is
 *  actively resizing and can afford to be generous; this is one still
 *  picture, and its leading should be the one it actually paints. */
const LABEL_LINE_RATIO = 1.2;

/** THE FLOOR, and why the picture stops shrinking rather than fading.
 *
 *  `labelFit`'s own floor is 10pt out of a 28pt base — the point below
 *  which the app stops shrinking a word and starts fading it out, because
 *  on a phone at 1:1 it has become texture rather than text. Carried over
 *  at this renderer's own ceiling that is 22 * 10/28, and it is written as
 *  that arithmetic so the two floors can never drift into two opinions.
 *
 *  NO FADE HERE, deliberately, and this is the one place the renderer
 *  refuses to copy the app. The app fades because the region is moving
 *  under the visitor's thumb and a word that has run out of room will have
 *  room again in a moment. A shared picture has no next moment: an invisible
 *  label in the one image a recipient ever sees is worse than a small one.
 *  So the size stops at the floor and the words stay, even where that means
 *  a monstrous label reaches past its region's curve — which is a picture
 *  that is honestly too full, not a picture that lies about its contents. */
const MIN_LABEL_FONT_SIZE = (LABEL_FONT_SIZE * FLOOR_SIZE) / BASE_SIZE;

/** Baloo 2 Regular's own vertical metrics, in units of the drawn size.
 *  Handed to `typesetsFor` because it asks for a face; see the call site
 *  for what this renderer does and does not use of the result. */
const BALOO_FACE = {
  ascent: BALOO2_ASCENDER / BALOO2_UNITS_PER_EM,
  descent: BALOO2_DESCENDER / BALOO2_UNITS_PER_EM,
};

/** Named, honestly, with real fallbacks — no @font-face, no external URL.
 *  The browser viewer supplies "Baloo 2" itself; the rasterizer bundles it
 *  (Task 10's worker half); this function only ever writes the family
 *  name, per the plan's "no external font/image refs" line.
 *
 *  SINGLE-quoted family names, deliberately: this whole string is placed
 *  inside a DOUBLE-quoted XML attribute value (`font-family="..."`), and a
 *  literal `"` in the value — even in a constant no attacker ever
 *  touches — closes the attribute early and breaks well-formedness. CSS
 *  accepts single quotes around a family name exactly as well as double. */
const FONT_STACK =
  "'Baloo 2', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

/** XML-escapes the five characters that matter for both text content and
 *  quoted-attribute contexts. This is the ENTIRE defense against the
 *  malicious-label case (a `<script>` payload, an attribute-breakout
 *  attempt): every string this module emits is routed through here before
 *  it reaches the output, so escaping this once closes both. */
function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** Formats a document-space number for the SVG. Rounds to 1/10,000 of a
 *  document unit — sub-hundredth-of-a-pixel at any real screen density —
 *  which keeps golden files free of the long float tails regions.ts's
 *  search geometry (sqrt-based anchors, a hill-climb with a 0.55 step
 *  decay) can otherwise produce for two numerically-equal positions, while
 *  costing nothing a viewer could ever see. Guards non-finite input rather
 *  than trusting the caller went through parseDocument, which enforces
 *  finiteness on `x`/`y`/`r` but is not the only way this function can be
 *  called.
 *
 *  A SECOND guard, past the input check (Ferruccio's gate, PR #65 round
 *  three, blocking finding 1): `Number.isFinite(n)` only proves `n` ITSELF
 *  is finite, not that `n * 10000` is. `parseDocument` places no upper
 *  bound on `x`/`y`/`r` (there is no MAX_COORDINATE in document.ts), so an
 *  admissible, honestly-parsed document can hold `r: 1e308` — finite,
 *  legal, and large enough that multiplying by the rounding factor
 *  overflows to `Infinity` (`Number.MAX_VALUE` is ~1.7976931348623157e308;
 *  anything past roughly a ten-thousandth of that, ~1.7976931348623157e304,
 *  overflows once scaled). Without this second check, `num` would have
 *  strung the literal text "Infinity" into an SVG attribute — worse than
 *  wrong geometry, an actively malformed number token.
 *
 *  DESIGNED FALLBACK, stated rather than left as an accident of which
 *  branch happened to run: when rounding would overflow, this function
 *  returns the RAW value's own `String(n)` — unrounded, but still the
 *  finite number the document actually holds — rather than clamping it to
 *  some invented ceiling or collapsing it to 0. Zero would silently turn
 *  an enormous (if absurd) circle into a single point; a clamp would
 *  silently turn it into a DIFFERENT enormous circle at a ceiling this
 *  function invented. Neither is more honest than simply skipping the
 *  cosmetic rounding step for a number so large that a ten-thousandth of
 *  a unit was never going to be visible on it anyway. (`String(n)` may
 *  render in exponential notation — e.g. `1e+21` — for a large enough
 *  magnitude; see the note on that at Task 10's rasterizer task, which is
 *  where a real consumer of this number first exists to have an opinion
 *  about it.) */
function num(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const product = n * 10000;
  if (!Number.isFinite(product)) return String(n);
  const rounded = Math.round(product) / 10000;
  // Math.round(-0 * 10000) is -0; String(-0) is "0", so no special case is
  // needed for the sign, only for the round-trip through String itself.
  return String(rounded === 0 ? 0 : rounded);
}

/** Picks the pinned lens color for a region from the SET of distinct scoop
 *  indices its member sets carry — not from the count of sets in the
 *  region, which the plan's own "2- and 3-set cases" language is a
 *  shorthand for, since `scoop` only ever holds 0, 1, or 2 (three colors,
 *  MAX_SETS = 6 circles). This generalizes cleanly to a document with more
 *  than 3 sets sharing scoops without inventing a new rule: a region whose
 *  sets happen to span 3 distinct scoop indices is the same "all three
 *  meet here" case regardless of whether it took 3 sets or more to reach.
 *  Two sets that happen to SHARE a scoop index contribute one distinct
 *  index between them, so a same-scoop overlap falls back to that scoop's
 *  own plain color rather than an undefined pair key — there is no lens
 *  entry for "a color overlapping itself", and there should not be one:
 *  nothing distinguishes that area from the circle's own interior. */
function regionFill(scoopIndices: ReadonlySet<number>): string {
  const sorted = [...scoopIndices].sort((a, b) => a - b);
  if (sorted.length <= 1) {
    return color.scoops[sorted[0] ?? 0]!;
  }
  if (sorted.length === 2) {
    const key = `pair${sorted[0]}${sorted[1]}` as keyof typeof color.lens;
    return color.lens[key] ?? color.lens.triple;
  }
  return color.lens.triple;
}

/** Builds the SVG for one region's exact area: a full-canvas rect, clipped
 *  to the intersection of every INCLUDED circle, with `maskId` (when given)
 *  referencing a `<mask>` the caller has already built to subtract every
 *  EXCLUDED circle — true circular arcs, no polygon approximation.
 *
 *  Intersection via NESTED clip-path: SVG has one `clip-path` attribute per
 *  element, so N included circles chain as N nested `<g>`s, each clipping
 *  the one inside it — the standard construction for "clip to the AND of
 *  several shapes" with no boolean-geometry library.
 *
 *  Subtraction via MASK, built by the caller (`renderVennSvg`, where the
 *  excluded circles are already in scope): SVG has no native clip-path
 *  NOT, so exclusion is a luminance mask — a white (fully-visible)
 *  full-canvas rect with a black (fully-hidden) circle painted for every
 *  excluded set. This function only references that mask by id; it never
 *  needs the excluded circles themselves. Both techniques are exact: the
 *  rendered edge is the circle's own mathematical boundary, not a sampled
 *  polygon standing in for it. */
function regionPaint(
  width: number,
  height: number,
  included: readonly string[],
  fill: string,
  clipIdFor: ReadonlyMap<string, string>,
  maskId: string | null,
): string {
  let open = '';
  let close = '';
  for (const id of included) {
    open += `<g clip-path="url(#${clipIdFor.get(id)!})">`;
    close = '</g>' + close;
  }
  const maskAttr = maskId ? ` mask="url(#${maskId})"` : '';
  const rect = `<rect x="0" y="0" width="${num(width)}" height="${num(height)}" fill="${fill}"${maskAttr}/>`;
  return `${open}${rect}${close}`;
}

/** One region's words, resolved: the lines to paint, top to bottom, and
 *  the one size they are all painted at. */
interface FittedLabels {
  lines: readonly string[];
  size: number;
}

/**
 * HOW A REGION'S WORDS FIND THEIR SIZE — `labelFit`'s rule, at this
 * renderer's altitude (issues 300 and 289).
 *
 * Before this, `renderVennSvg` set every label as ONE line at a fixed 22
 * and stacked N of them at a fixed pitch. Both halves broke in the open,
 * on the same day: Mathew opened his own shared link in Safari and three
 * region labels overlapped into an unreadable smear (300), and a region
 * holding eight items would have drawn 211 document units of stack inside
 * a lens about 200 tall (289). The viewer is the first thing anyone
 * without the app ever sees of a venn, so this is the public face of the
 * toy, not a cosmetic edge.
 *
 * THE RULE IS THE APP'S, AND IT IS THE ELLIPSE. `regionsFor` hands over a
 * `budget` whose own docstring warns it is NOT a true inscribed rectangle:
 * its height is measured at the anchor's exact axis offset, so the box's
 * corners poke past the bounding curve even when its centre line fits.
 * `fitScale` reads it conservatively — a block fits when its own corner
 * lands inside the ellipse, `(w/W)² + (h/H)² <= 1` — which is the reading
 * that docstring asks for.
 *
 * THE STACK SCALES AS ONE BLOCK, which is 289's own prescription and also
 * the only thing that reads as a list rather than a ransom note: N labels
 * in one region are N sizes if you fit them separately, and one size if
 * you fit the block they make. So the pass is two-step, deliberately:
 *
 *  1. Each label chooses its LINE BREAKING against an equal slice of the
 *     region's height. The slice is an approximation — the region is an
 *     ellipse, not a stack of equal rectangles — and it does not need to
 *     be anything better, because it decides only WHERE THE WORDS BREAK.
 *     The size it implies is thrown away. The choosing is `biggestFit`,
 *     the same function the app's drawing reaches through `chooseTypeset`;
 *     this file contains no comparison loop of its own.
 *  2. The chosen lines are then measured as ONE block — widest line by
 *     total height — against the WHOLE budget, exactly once, by
 *     `drawnSize`. That number is the size, and it is exact under the
 *     ellipse rule no matter how rough step 1 was.
 *
 * THERE IS NO FITTING ARITHMETIC IN THIS FILE, and that is the point of
 * the shape rather than an accident of it. Every number above comes out of
 * `labelFit` — the candidate breakings, the re-statement at this
 * renderer's leading, the comparison, the ellipse. What is left here is
 * assembly: which words belong to which region, and where the baselines
 * go. Core owns the layout; this is one of its two skins.
 *
 * WHAT IT COSTS, because this function is now on the thumbnail worker's
 * per-request path and `typesetsFor` measures k²/2 word runs per label.
 * Measured on this Mac (node 26, 2026-09-03, mean of 100 renders):
 * `two-set` 0.022ms, `long-labels` 0.077ms, `region-list` 0.048ms, the
 * `max-labels` golden — MAX_LABELS = 128 short labels — 0.511ms. The
 * worst case the corpus does not hold, 128 ten-word labels all in one
 * region, is 8.1ms. A render was always going to be the cheap half of
 * rasterizing one; no cache is warranted and none is here.
 *
 * `typesetsFor` is given the face's honest metrics because it asks for a
 * face, and this function then ignores the `blockH`/`lineHeight`/
 * `halfLeading` it computes from them: those describe Baloo's designed
 * line box (1.602 em, sized for Devanagari), and this renderer lays out at
 * its own measured `LABEL_LINE_RATIO`. Nothing about the line BREAKING is
 * lost by that — `typesetsFor` enumerates candidate breakings by width
 * alone and never consults the metrics to choose between them.
 */
function fitRegionLabels(
  texts: readonly string[],
  budget: { width: number; height: number },
): FittedLabels {
  // Measured AT THE CEILING, so every extent below is already in document
  // units and the size `drawnSize` returns is in them too.
  const measure = (run: string) => balooEmWidth(run) * LABEL_FONT_SIZE;

  const share = budget.height / texts.length;
  const chosen: Typeset[] = [];
  for (const text of texts) {
    const typesets = typesetsFor(measure, BALOO_FACE, text);
    // `extentsAt` re-states each candidate at THIS renderer's leading
    // (see its own comment for why the face's designed line box is the
    // wrong ruler for a still picture), and `biggestFit` is the one
    // comparison loop in the repo — the drawing calls it too, through
    // `chooseTypeset`. A label of nothing but whitespace has no candidate
    // at all and `biggestFit` says -1, so it draws nothing rather than an
    // empty `<text>` node.
    const pick = biggestFit(
      extentsAt(typesets, LABEL_FONT_SIZE, LABEL_FONT_SIZE, LABEL_LINE_RATIO),
      budget.width,
      share,
    );
    if (pick >= 0) chosen.push(typesets[pick]!);
  }

  const totalLines = chosen.reduce((n, t) => n + t.lines.length, 0);
  if (totalLines === 0) return { lines: [], size: LABEL_FONT_SIZE };

  const blockW = Math.max(...chosen.map((typeset) => typeset.blockW));
  return {
    // `drawnSize` is capped at its own base, so this can only ever SHRINK:
    // a label that already fits is still set at exactly LABEL_FONT_SIZE,
    // which is what keeps every already-fitting golden byte-identical.
    size: Math.max(
      MIN_LABEL_FONT_SIZE,
      drawnSize(
        blockW,
        totalLines * LABEL_FONT_SIZE * LABEL_LINE_RATIO,
        budget.width,
        budget.height,
        LABEL_FONT_SIZE,
      ),
    ),
    lines: chosen.flatMap((typeset) => typeset.lines.map((line) => line.text)),
  };
}

/**
 * Renders `doc` at rest as a complete, self-contained SVG document string.
 * Pure: no clock, no RNG, no platform API — the same document always
 * produces the exact same bytes, on any of the (bit-identical) engines
 * this repo's determinism law cares about, because every number that
 * reaches the output has already passed through `num`'s fixed rounding,
 * and every id that reaches it has already passed through `opts.idPrefix`'s
 * own validation (which throws rather than silently repairing a bad
 * caller value — see `ID_PREFIX_PATTERN`'s own comment).
 *
 * WIDTH/HEIGHT, A CAVEAT FOR THIS FUNCTION'S TWO CALLERS (Severino's ride,
 * finding #4/#5, advisory — noted here for whoever builds each task): the
 * root `<svg>`'s `width`/`height` attributes are the DOCUMENT's own canvas
 * size, unscaled — see the module comment's "VIEWBOX / SIZING CONVENTION"
 * section. Neither caller should read them as a rendering instruction:
 * the `workers/` thumbnail (Task 10's rasterizer half) picks its OWN
 * pixmap dimensions independently and must not assume this string's
 * width/height are the output resolution it wants; Task 11's viewer camera
 * is a CSS transform over the whole inlined `<svg>` and will NOT get a
 * free viewBox-driven scale-to-fit from these attributes alone without its
 * own explicit sizing on the containing element.
 */
export function renderVennSvg(
  doc: VennDocumentV1,
  opts?: RenderVennSvgOptions,
): string {
  const idPrefix = opts?.idPrefix ?? '';
  if (!ID_PREFIX_PATTERN.test(idPrefix)) {
    throw new Error(
      `renderVennSvg: idPrefix must match ${ID_PREFIX_PATTERN} (letters, digits, "_", "-"); got ${JSON.stringify(idPrefix)}`,
    );
  }

  const { width, height } = doc.canvas;
  // Validated only when it was actually GIVEN. The default is the
  // document's own canvas, and this function has never had an opinion
  // about that — a hand-built zero-width canvas rendered before this
  // option existed and must keep rendering, or an additive option would
  // have quietly become a new document rule.
  const frame = opts?.viewBox ?? { x: 0, y: 0, width, height };
  if (
    opts?.viewBox !== undefined &&
    (!Number.isFinite(frame.x) ||
      !Number.isFinite(frame.y) ||
      !Number.isFinite(frame.width) ||
      !Number.isFinite(frame.height) ||
      frame.width <= 0 ||
      frame.height <= 0)
  ) {
    throw new Error(
      `renderVennSvg: viewBox must be a finite box with a positive extent; got ${JSON.stringify(opts.viewBox)}`,
    );
  }

  const named: NamedCircle[] = doc.sets.map((s) => ({
    id: s.id,
    circle: { center: { x: s.x, y: s.y }, radius: s.r },
  }));
  const scoopById = new Map<string, number>(
    doc.sets.map((s) => [s.id, s.scoop]),
  );
  const setById = new Map(doc.sets.map((s) => [s.id, s]));
  const allIds = doc.sets.map((s) => s.id);

  const defs: string[] = [];
  const clipIdFor = new Map<string, string>();
  doc.sets.forEach((s, i) => {
    // INDEX-based, never doc-content-based (Bianca's gate, PR #65 note 3),
    // NAMESPACED by the caller's idPrefix (Severino's ride, finding #1 —
    // see the module comment's "ID NAMESPACE COLLISIONS" section). `id`
    // does not land in XML text or a quoted attribute VALUE — it lands
    // inside `url(#...)` functional notation, a CSS grammar with its own
    // metacharacters (`(`, `)`, whitespace, `#` mid-token) that XML
    // escaping has no opinion about. A set id of `a)` — legal XML-escaped,
    // illegal-shaped `url()` — would have produced `url(#clip-a))`, a
    // malformed reference, not a working clip. Escaping harder in the
    // SAME alphabet does not fix a wrong alphabet; the actual fix is to
    // never let doc content reach this position at all. An index is
    // internally minted, never touches the document, and removes the
    // entire class of "what does THIS context's grammar consider
    // dangerous" questions rather than answering it once per context.
    const cid = `${idPrefix}clip-${i}`;
    clipIdFor.set(s.id, cid);
    // clipPathUnits="userSpaceOnUse" pinned explicitly (Severino's ride,
    // finding #2, advisory — taken): it is already the spec DEFAULT for
    // clipPath, so this changes nothing observable, but resvg/usvg and
    // Safari have real history disagreeing with each other and with the
    // spec over unstated defaults under nested clip+mask — stating it
    // removes the ambiguity a rasterizer could otherwise resolve wrong.
    defs.push(
      `<clipPath id="${cid}" clipPathUnits="userSpaceOnUse"><circle cx="${num(s.x)}" cy="${num(s.y)}" r="${num(s.r)}"/></clipPath>`,
    );
  });

  const regions = regionsFor(named);

  const paints: string[] = [];
  let maskCounter = 0;
  for (const region of regions) {
    const included = region.key;
    const includedSet = new Set(included);
    const excludedIds = allIds.filter((id) => !includedSet.has(id));
    // The non-null assertion that used to stand here contradicted the
    // filter beside it (issue 229): one of them had to be wrong, and it
    // was the assertion — a `!` says "this cannot miss" while the very
    // next call says "and when it does". Keeping the filter and dropping
    // the claim is the honest direction; a miss is impossible today
    // (`scoopById` and `named` are built from the same doc.sets) and this
    // way it stays a dropped region rather than an `undefined` in a set
    // of scoop indices if that ever stops being true.
    const scoopIndices = new Set(
      included
        .map((id) => scoopById.get(id))
        .filter((v): v is number => v !== undefined),
    );
    const fill = regionFill(scoopIndices);

    let maskId: string | null = null;
    if (excludedIds.length > 0) {
      maskId = `${idPrefix}mask-${maskCounter++}`;
      const circles = excludedIds
        .map((id) => {
          const s = setById.get(id)!;
          return `<circle cx="${num(s.x)}" cy="${num(s.y)}" r="${num(s.r)}" fill="black"/>`;
        })
        .join('');
      // maskUnits="userSpaceOnUse" PLUS an explicit x/y/width/height
      // covering the whole canvas (Severino's ride, finding #2, advisory
      // — taken): unlike clipPathUnits, mask's own bounding box DEFAULTS
      // to objectBoundingBox with a -10%/120% fudge margin relative to
      // whatever it is applied to — exactly the unstated-default territory
      // where resvg/usvg and Safari have a documented history of
      // disagreeing under nested clip+mask. Pinning it to the literal
      // canvas rect in USER space removes that guesswork entirely.
      defs.push(
        `<mask id="${maskId}" maskUnits="userSpaceOnUse" x="0" y="0" width="${num(width)}" height="${num(height)}"><rect x="0" y="0" width="${num(width)}" height="${num(height)}" fill="white"/>${circles}</mask>`,
      );
    }

    paints.push(regionPaint(width, height, included, fill, clipIdFor, maskId));
  }

  // Labels: only ones whose region key currently exists get to rest
  // somewhere; homeless ("orphan") labels are skipped — see the module
  // comment for why this function does not invent a re-placement policy.
  const regionByKey = new Map(regions.map((r) => [regionId(r.key), r]));
  const labelsByRegion = new Map<string, string[]>();
  for (const label of doc.labels) {
    const key = regionId(label.members);
    if (!regionByKey.has(key)) continue;
    const bucket = labelsByRegion.get(key) ?? [];
    bucket.push(label.text);
    labelsByRegion.set(key, bucket);
  }

  // font-family/font-size/fill hoisted to ONE wrapping <g> rather than
  // repeated on every <text> (Severino's ride, finding #4/#5, advisory —
  // taken): a cascade shield (one place to read what every label looks
  // like) and a byte cut, with no behavior change — SVG's presentation
  // attributes inherit through <g> exactly as they would if repeated per
  // element.
  const texts: string[] = [];
  for (const [key, labels] of labelsByRegion) {
    const { anchor, budget } = regionByKey.get(key)!;
    const fitted = fitRegionLabels(labels, budget);
    const lineHeight = fitted.size * LABEL_LINE_RATIO;
    const totalHeight = fitted.lines.length * lineHeight;
    // THE GROUP STATES THE RESTING SIZE; A LABEL THAT HAD TO SHRINK SAYS
    // SO ON ITSELF. Emitting `font-size` unconditionally would have been
    // one line shorter and would have rewritten every `<text>` node in
    // every golden for a change that moved nothing — so the attribute is
    // absent exactly when the answer is the group's own 22, and a diff of
    // the goldens then shows only the labels this round actually changed.
    const sizeAttr =
      fitted.size === LABEL_FONT_SIZE
        ? ''
        : ` font-size="${num(fitted.size)}px"`;
    fitted.lines.forEach((line, i) => {
      const y =
        anchor.y -
        totalHeight / 2 +
        lineHeight * (i + 0.5) +
        fitted.size * 0.35; // baseline offset: roughly text-mid to alphabetic baseline
      texts.push(
        `<text x="${num(anchor.x)}" y="${num(y)}"${sizeAttr} text-anchor="middle">${escapeXml(line)}</text>`,
      );
    });
  }
  const textGroup =
    texts.length > 0
      ? `<g font-family="${FONT_STACK}" font-size="${LABEL_FONT_SIZE}px" fill="${color.ink}">${texts.join('')}</g>`
      : '';

  // <title>/<desc> as the FIRST children of <svg> (Severino's ride,
  // finding #3, advisory — taken): accessibility/unfurl metadata, not
  // rendered chrome — see the module comment's "TITLE/DESC ARE METADATA"
  // section for why this does not reopen the earlier no-visible-title
  // decision. A document with no `title` gets a designed, generic
  // fallback rather than an empty element — "a venn diagram" is still a
  // true, useful sentence for a screen reader or an unfurl with nothing
  // else to say.
  const titleText =
    doc.title && doc.title.length > 0 ? doc.title : 'a venn diagram';
  const descText = `${doc.sets.length} set${doc.sets.length === 1 ? '' : 's'}, ${doc.labels.length} label${doc.labels.length === 1 ? '' : 's'}`;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" role="img" viewBox="${num(frame.x)} ${num(frame.y)} ${num(frame.width)} ${num(frame.height)}" width="${num(frame.width)}" height="${num(frame.height)}">` +
    `<title>${escapeXml(titleText)}</title>` +
    `<desc>${escapeXml(descText)}</desc>` +
    `<rect x="0" y="0" width="${num(width)}" height="${num(height)}" fill="${color.ground}"/>` +
    (defs.length > 0 ? `<defs>${defs.join('')}</defs>` : '') +
    paints.join('') +
    textGroup +
    `</svg>`
  );
}
